import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

// Detectar si estamos en app nativa
export const isNative = Capacitor.isNativePlatform();
export const isAndroid = Capacitor.getPlatform() === 'android';
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isWeb = Capacitor.getPlatform() === 'web';

// Inicializar la app móvil
export const initializeApp = async () => {
  if (!isNative) return;
  
  try {
    // Configurar StatusBar oscuro
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#09090b' });
    
    // Ocultar splash después de cargar
    await SplashScreen.hide();
  } catch (error) {
    // StatusBar/SplashScreen not available on this platform
    console.debug('Capacitor StatusBar/SplashScreen not available:', error.message);
  }
};

// Vibración háptica para feedback táctil
export const hapticFeedback = async (type = 'light') => {
  if (!isNative) return;
  
  try {
    const styles = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    };
    await Haptics.impact({ style: styles[type] || ImpactStyle.Light });
  } catch (error) {
    // Haptics not available on this platform
    console.debug('Capacitor Haptics not available:', error.message);
  }
};

// Compartir contenido (PDFs, etc.)
export const shareContent = async (options) => {
  try {
    if (isNative) {
      await Share.share({
        title: options.title,
        text: options.text,
        url: options.url,
        dialogTitle: options.dialogTitle || 'Compartir'
      });
    } else {
      // Fallback para web
      if (navigator.share) {
        await navigator.share({
          title: options.title,
          text: options.text,
          url: options.url
        });
      } else {
        // Copiar al portapapeles
        await navigator.clipboard.writeText(options.url || options.text);
        return { copied: true };
      }
    }
    return { success: true };
  } catch (error) {
    return { error };
  }
};

// Descargar archivo (PDFs)
export const downloadFile = async (url, filename) => {
  try {
    if (isNative) {
      // En app nativa, descargar y guardar
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Convertir blob a base64
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(blob);
      });
      
      // Guardar archivo
      const result = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Documents
      });
      
      return { success: true, path: result.uri };
    } else {
      // En web, descargar normalmente
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return { success: true };
    }
  } catch (error) {
    return { error };
  }
};

// Safe area insets para notch/home indicator
export const getSafeAreaInsets = () => {
  if (typeof window === 'undefined') return { top: 0, bottom: 0, left: 0, right: 0 };
  
  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('--sat') || '0', 10),
    bottom: parseInt(style.getPropertyValue('--sab') || '0', 10),
    left: parseInt(style.getPropertyValue('--sal') || '0', 10),
    right: parseInt(style.getPropertyValue('--sar') || '0', 10)
  };
};

// Detectar orientación del dispositivo
export const getOrientation = () => {
  if (typeof window === 'undefined') return 'portrait';
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
};

// Detectar si es tablet
export const isTablet = () => {
  if (typeof window === 'undefined') return false;
  const minDimension = Math.min(window.innerWidth, window.innerHeight);
  return minDimension >= 600;
};
